/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';

export const HOOK_CONFIG_DISCRIMINATOR = new Uint8Array([
  137, 155, 101, 95, 138, 72, 8, 182,
]);

export function getHookConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(HOOK_CONFIG_DISCRIMINATOR);
}

export type HookConfig = {
  discriminator: ReadonlyUint8Array;
  adminAuthority: Address;
  initializerProgram: Address;
  rehypeRouterProgram: Address;
  cosignerCount: number;
  bump: number;
  version: number;
  reserved: ReadonlyUint8Array;
  cosigners: Array<Address>;
};

export type HookConfigArgs = {
  adminAuthority: Address;
  initializerProgram: Address;
  rehypeRouterProgram: Address;
  cosignerCount: number;
  bump: number;
  version: number;
  reserved: ReadonlyUint8Array;
  cosigners: Array<Address>;
};

/** Gets the encoder for {@link HookConfigArgs} account data. */
export function getHookConfigEncoder(): FixedSizeEncoder<HookConfigArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['adminAuthority', getAddressEncoder()],
      ['initializerProgram', getAddressEncoder()],
      ['rehypeRouterProgram', getAddressEncoder()],
      ['cosignerCount', getU8Encoder()],
      ['bump', getU8Encoder()],
      ['version', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 29)],
      ['cosigners', getArrayEncoder(getAddressEncoder(), { size: 32 })],
    ]),
    (value) => ({ ...value, discriminator: HOOK_CONFIG_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link HookConfig} account data. */
export function getHookConfigDecoder(): FixedSizeDecoder<HookConfig> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['adminAuthority', getAddressDecoder()],
    ['initializerProgram', getAddressDecoder()],
    ['rehypeRouterProgram', getAddressDecoder()],
    ['cosignerCount', getU8Decoder()],
    ['bump', getU8Decoder()],
    ['version', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 29)],
    ['cosigners', getArrayDecoder(getAddressDecoder(), { size: 32 })],
  ]);
}

/** Gets the codec for {@link HookConfig} account data. */
export function getHookConfigCodec(): FixedSizeCodec<
  HookConfigArgs,
  HookConfig
> {
  return combineCodec(getHookConfigEncoder(), getHookConfigDecoder());
}

export function decodeHookConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<HookConfig, TAddress>;
export function decodeHookConfig<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<HookConfig, TAddress>;
export function decodeHookConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<HookConfig, TAddress> | MaybeAccount<HookConfig, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getHookConfigDecoder(),
  );
}

export async function fetchHookConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<HookConfig, TAddress>> {
  const maybeAccount = await fetchMaybeHookConfig(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeHookConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<HookConfig, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeHookConfig(maybeAccount);
}

export async function fetchAllHookConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<HookConfig>[]> {
  const maybeAccounts = await fetchAllMaybeHookConfig(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeHookConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<HookConfig>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeHookConfig(maybeAccount));
}

export function getHookConfigSize(): number {
  return 1160;
}
